import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useMe } from "../api/queries/users.queries";
import { useDocumentStore } from "../stores/document.store";
import {
	useEmailActions,
	useEmailComposerMode,
	useIsEmailComposerOpen,
	useSelectedEmailId,
} from "../stores/email.store";
import { useSelectedTaskId, useTaskActions } from "../stores/task.store";
import type { PanelSyncConfig } from "../types/panel.types";
import { registerEntityPanels } from "../utils/panel-registry";
import { useDocumentSelection } from "./panel-sync/useDocumentSelection";
import { useEmailSelection } from "./panel-sync/useEmailSelection";
import { useTaskSelection } from "./panel-sync/useTaskSelection";
import { usePanel } from "./usePanel";
import { RoleEnum } from "../../../../packages/vars/src";

interface EntityPanelSyncReturn {
	email: {
		selectedEmailId: string | null;
		clearEmailSelection: () => void;
	};
	task: {
		selectedTaskId: string | null;
		clearTaskSelection: () => void;
	};
	document: {
		selectedDocumentUrl: string | undefined;
		clearDocument: () => void;
	};
	clearAllSelections: () => void;
	selectedEmailId: string | null;
	selectedTaskId: string | null;
	selectedDocumentUrl: string | undefined;
}

export const useEntityPanelSync = (
	config: PanelSyncConfig,
): EntityPanelSyncReturn => {
	const {
		entityType,
		entityId,
		communications = [],
		tasks = [],
		customComponents,
		emailRoutingUuid,
		components,
		emailConfig,
		documentConfig,
	} = config;

	const panel = usePanel();
	const { me } = useMe();

	const emailConfigRef = useRef(emailConfig);
	const communicationsRef = useRef(communications);
	const tasksRef = useRef(tasks);
	const customComponentsRef = useRef(customComponents);

	emailConfigRef.current = emailConfig;
	communicationsRef.current = communications;
	tasksRef.current = tasks;
	customComponentsRef.current = customComponents;

	useEffect(() => {
		if (components) {
			registerEntityPanels({
				entityType,
				components,
				emailConfig,
			});
		}
	}, [entityType, components, emailConfig]);

	const { clearEmailSelection, clearComposerState } = useEmailActions();
	const { clearTaskSelection } = useTaskActions();
	const { clearDocument } = useDocumentStore();

	const selectedEmailId = useSelectedEmailId();
	const selectedTaskId = useSelectedTaskId();
	const selectedDocumentUrl = useDocumentStore(
		(state) => state.selectedDocumentUrl,
	);

	useEffect(() => {
		return () => {
			panel.clearPanel();
			clearEmailSelection();
			clearComposerState();
			clearTaskSelection();
			clearDocument();
		};
	}, [
		panel,
		clearEmailSelection,
		clearComposerState,
		clearTaskSelection,
		clearDocument,
	]);

	const isComposerOpen = useIsEmailComposerOpen();
	const composerMode = useEmailComposerMode();

	const clearOtherSelections = useCallback(
		(except?: "email" | "task" | "document") => {
			if (except !== "email") clearEmailSelection();
			if (except !== "task") clearTaskSelection();
			if (except !== "document") clearDocument();
		},
		[clearEmailSelection, clearTaskSelection, clearDocument],
	);

	useEffect(() => {
		if (!entityType || !entityId || !isComposerOpen || !composerMode) return;

		const currentEmailConfig = emailConfigRef.current;

		const getReplyToEmail = currentEmailConfig?.getReplyToEmail;
		if (!getReplyToEmail) {
			const errorMessage = `Configuration email manquante pour le type d'entité: ${entityType}`;
			console.warn(errorMessage);

			if (import.meta.env.DEV) {
				toast.error("Configuration manquante", {
					description:
						"Aucune configuration email fournie. Consultez la console pour plus de détails.",
					duration: 5000,
				});
			}
			return;
		}

		const replyToEmail = getReplyToEmail(entityId, emailRoutingUuid);

		panel.showEmailComposer({
			entityId: entityId,
			entityType: entityType,
			replyTo: replyToEmail,
			initialData: composerMode.initialData,
			draftId: composerMode.draftId,
			disabled: me?.role !== RoleEnum.DUNE,
		});
	}, [
		isComposerOpen,
		composerMode,
		entityType,
		entityId,
		emailRoutingUuid,
		panel,
		me?.role,
	]);

	const emailHandlers = useEmailSelection({
		entityType,
		entityId,
		communications: communicationsRef.current,
		emailConfig: emailConfigRef.current,
		customComponent: customComponentsRef.current?.EmailPreview,
		emailRoutingUuid,
	});

	const taskHandlers = useTaskSelection({
		entityType,
		entityId,
		tasks: tasksRef.current,
		customComponent: customComponentsRef.current?.TaskDetails,
	});

	const documentHandlers = useDocumentSelection({
		entityType,
		entityId,
		customComponent:
			customComponentsRef.current?.DocumentPreview ||
			components?.DocumentPreview,
		onDelete: documentConfig?.onDelete,
	});

	useEffect(() => {
		if (selectedEmailId) {
			clearTaskSelection();
			clearDocument();
		}
	}, [selectedEmailId, clearTaskSelection, clearDocument]);

	useEffect(() => {
		if (selectedTaskId) {
			clearEmailSelection();
			clearDocument();
		}
	}, [selectedTaskId, clearEmailSelection, clearDocument]);

	useEffect(() => {
		if (selectedDocumentUrl) {
			clearEmailSelection();
			clearTaskSelection();
		}
	}, [selectedDocumentUrl, clearEmailSelection, clearTaskSelection]);

	return {
		email: emailHandlers,
		task: taskHandlers,
		document: documentHandlers,

		clearAllSelections: () => clearOtherSelections(),

		selectedEmailId,
		selectedTaskId,
		selectedDocumentUrl,
	};
};
